<a
  class="oc-row"
  [routerLink]="reviewLink"
  [class.oc-row--done]="data?.status === 'DONE'"
  [class.oc-row--new]="isNew"
>
  <!-- Document -->
  <div class="oc-cell oc-doc-cell">
    <span class="oc-label">{{ 'AI_PARSER_DOCUMENT' | translate }}</span>
    <span *ngIf="fileName; else dash" class="oc-doc">
      <i class="fas {{ fileIconClass }} oc-doc-glyph" aria-hidden="true"></i>
      <span class="oc-doc-name text-truncate" [title]="fileName">{{ fileName }}</span>
    </span>
  </div>

  <!-- From -->
  <div class="oc-cell oc-from-cell">
    <span class="oc-label">{{ 'AI_PARSER_FROM' | translate }}</span>
    <div class="oc-from">
      <span class="oc-from-line">
        <i
          class="fas oc-source-glyph"
          [class.fa-envelope]="isEmail"
          [class.fa-arrow-up-from-bracket]="!isEmail"
          [title]="sourceTitle"
          aria-hidden="true"
        ></i>
        <span class="oc-from-sender text-truncate" [title]="sender">{{ sender }}</span>
      </span>
      <span *ngIf="subject" class="oc-from-subject text-truncate" [title]="subject">{{ subject }}</span>
    </div>
  </div>

  <!-- Received -->
  <div class="oc-cell oc-received-cell" [title]="receivedAt | date: 'yyyy-MM-dd HH:mm'">
    <span class="oc-label">{{ 'AI_PARSER_RECEIVED' | translate }}</span>
    <span class="oc-received">{{ relativeReceived }}</span>
  </div>

  <!-- Config -->
  <div class="oc-cell oc-config-cell">
    <span class="oc-label">{{ 'AI_PARSER_CONFIG' | translate }}</span>
    <span
      *ngIf="data?.assignedConfigName; else dash"
      class="oc-config text-truncate"
      [title]="data.assignedConfigName"
    >
      {{ data.assignedConfigName }}
    </span>
  </div>

  <!-- Order no. (the customer's PO number) -->
  <div class="oc-cell oc-order-cell">
    <span class="oc-label">{{ 'AI_PARSER_CUSTOMER_ORDER_NO' | translate }}</span>
    <span *ngIf="customerOrderNumber; else dash" class="oc-order-value">
      <span class="oc-order-no">{{ customerOrderNumber }}</span>
      <button
        type="button"
        class="oc-copy"
        (click)="copyOrderNumber(customerOrderNumber, 'customer', $event)"
        [title]="'AI_PARSER_COPY' | translate"
        [attr.aria-label]="'AI_PARSER_COPY' | translate"
      >
        <i class="fas" [ngClass]="copiedField === 'customer' ? 'fa-check' : 'fa-copy'" aria-hidden="true"></i>
      </button>
    </span>
  </div>

  <!-- ERP order no. — set once the order has been submitted -->
  <div class="oc-cell oc-erp-order-cell">
    <span class="oc-label">{{ 'AI_PARSER_ORDER_NO' | translate }}</span>
    <span *ngIf="erpOrderNumber; else dash" class="oc-order-value">
      <span class="oc-order-no">{{ erpOrderNumber }}</span>
      <button
        type="button"
        class="oc-copy"
        (click)="copyOrderNumber(erpOrderNumber, 'erp', $event)"
        [title]="'AI_PARSER_COPY' | translate"
        [attr.aria-label]="'AI_PARSER_COPY' | translate"
      >
        <i class="fas" [ngClass]="copiedField === 'erp' ? 'fa-check' : 'fa-copy'" aria-hidden="true"></i>
      </button>
    </span>
  </div>

  <!-- Rows -->
  <div class="oc-cell oc-rows-cell">
    <span class="oc-label">{{ 'AI_PARSER_ROWS' | translate }}</span>
    <span *ngIf="rowCount > 0; else dash" class="oc-rows">{{ rowCount }}</span>
  </div>

  <!-- Status -->
  <div class="oc-cell oc-status-cell">
    <span class="oc-label">{{ 'AI_PARSER_STATUS' | translate }}</span>
    <span class="oc-status">
      <lib-pdf-ai-parser-status-pill [status]="data?.status"></lib-pdf-ai-parser-status-pill>
      <span class="oc-warn-slot">
        <span *ngIf="data?.warnings?.length" class="oc-warn" [title]="warningsTitle">
          <i class="fas fa-exclamation-triangle" aria-hidden="true"></i>
        </span>
      </span>
      <!-- Why this order is in manual review (trust ramp / periodic check) -->
      <span *ngIf="trustReason as reason" class="oc-trust" [title]="trustBadgeTitle" [ngSwitch]="reason">
        <ng-container *ngSwitchCase="'TRUST_RAMP'">{{
          'AI_PARSER_TRUST_RAMP_BADGE' | translate: { count: trustCount, threshold: trustThreshold }
        }}</ng-container>
        <ng-container *ngSwitchCase="'PERIODIC_CHECK'">{{ 'AI_PARSER_PERIODIC_CHECK_BADGE' | translate }}</ng-container>
      </span>
    </span>
  </div>

  <!-- Action: an overflow (⋯) menu with Open / Delete. The whole row is still the anchor, so
       the cell stops click propagation to avoid navigating; the menu is rendered on the document
       body (container="body") to escape the list row's clipping. -->
  <div class="oc-cell oc-action-cell" (click)="stopRowNavigation($event)">
    <button
      *ngIf="canQuickSubmit"
      type="button"
      class="btn btn-sm oc-submit"
      [disabled]="isSubmitting"
      [title]="'AI_PARSER_ACTION_SUBMIT' | translate"
      (click)="askSubmit($event)"
    >
      <span *ngIf="isSubmitting" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
      {{ 'AI_PARSER_ACTION_SUBMIT' | translate }}
    </button>
    <div ngbDropdown container="body" class="oc-menu">
      <button
        type="button"
        class="btn btn-sm oc-menu-toggle"
        ngbDropdownToggle
        [title]="'AI_PARSER_ACTIONS' | translate"
        aria-label="Actions"
        (click)="stopRowNavigation($event)"
      >
        <i class="fas fa-ellipsis-h" aria-hidden="true"></i>
      </button>
      <div ngbDropdownMenu class="oc-menu-list">
        <button type="button" ngbDropdownItem (click)="open()">
          {{ 'AI_PARSER_ACTION_OPEN' | translate }}
        </button>
        <button type="button" ngbDropdownItem class="text-danger" (click)="askDelete($event)">
          {{ 'AI_PARSER_ACTION_DELETE' | translate }}
        </button>
      </div>
    </div>
  </div>
</a>

<ng-template #dash><span class="oc-dash">&mdash;</span></ng-template>

<ng-template #deleteModal let-modal>
  <div class="modal-header">
    <h5 class="modal-title">{{ 'AI_PARSER_DELETE_CONFIRM_TITLE' | translate }}</h5>
    <button type="button" class="btn-close" (click)="modal.dismiss()"></button>
  </div>
  <div class="modal-body">
    <p>{{ 'AI_PARSER_DELETE_CONFIRM_BODY' | translate:{ name: fileName } }}</p>
    <p *ngIf="hasErpOrder" class="text-muted small mb-0">
      {{ 'AI_PARSER_DELETE_ERP_NOTE' | translate:{ orderNo: erpOrderNumber } }}
    </p>
  </div>
  <div class="modal-footer">
    <button type="button" class="btn btn-outline-secondary" (click)="modal.dismiss()">
      {{ 'CANCEL' | translate }}
    </button>
    <button type="button" class="btn btn-danger" (click)="modal.close()">
      {{ 'DELETE' | translate }}
    </button>
  </div>
</ng-template>

<ng-template #submitModal let-modal>
  <div class="modal-header">
    <h5 class="modal-title">{{ 'AI_PARSER_SUBMIT_CONFIRM_TITLE' | translate }}</h5>
    <button type="button" class="btn-close" (click)="modal.dismiss()"></button>
  </div>
  <div class="modal-body">
    <p>{{ 'AI_PARSER_SUBMIT_CONFIRM_BODY' | translate:{ name: fileName } }}</p>
    <p class="text-muted small mb-0">
      {{ 'AI_PARSER_SUBMIT_CONFIRM_COUNTS_NOTE' | translate }}
    </p>
  </div>
  <div class="modal-footer">
    <button type="button" class="btn btn-outline-secondary" (click)="modal.dismiss()">
      {{ 'CANCEL' | translate }}
    </button>
    <button type="button" class="btn btn-primary" (click)="modal.close()">
      {{ 'AI_PARSER_ACTION_SUBMIT' | translate }}
    </button>
  </div>
</ng-template>
